/* ============================================================
   VIORAH – Main JavaScript
   ============================================================ */
'use strict';

/* ---- Utility ---- */
const qs  = (sel, ctx = document) => ctx.querySelector(sel);
const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

/* ============================================================
   CART AJAX
   ============================================================ */
class ViorahCart {
  static async addItem(formData, properties) {
    const routes = window.Viorah?.routes || {};
    const body = { id: formData.get('id'), quantity: 1 };
    if (properties && Object.keys(properties).length) body.properties = properties;
    const res = await fetch(routes.cart_add || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error('Cart error');
    return res.json();
  }

  static updateCount(n) {
    qsa('.cart-count').forEach(el => {
      el.textContent = n;
      el.style.display = n > 0 ? 'flex' : 'none';
    });
  }

  static async changeItem(key, quantity) {
    const routes = window.Viorah?.routes || {};
    const res = await fetch(routes.cart_change || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!res.ok) throw new Error('Cart error');
    return res.json();
  }
}

/* ============================================================
   ANIMAL SWITCH – swap to a different animal's product without
   a full page reload (pills link to separate products, not variants)
   ============================================================ */
function initAnimalSwitch() {
  const pills = qsa('.animal-pill[href]');
  if (!pills.length) return;

  const descEl       = qs('.product-desc');
  const priceWrap    = qs('#productPrice');
  const mainImg      = qs('#GalleryMainImg');
  const mainVideo     = qs('#GalleryMainVideo');
  const thumbsWrap    = qs('#GalleryThumbs');
  const variantInput  = qs('#variantId');
  const addBtn        = qs('#btnAddToCart');

  function money(cents) {
    return 'CHF ' + (cents / 100).toFixed(2);
  }

  function selectThumb(thumb, allThumbs) {
    allThumbs.forEach(t => t.classList.remove('active'));
    thumb.classList.add('active');
    if (thumb.dataset.videoUrl) {
      if (mainVideo) {
        mainVideo.setAttribute('src', thumb.dataset.videoUrl);
        mainVideo.style.display = 'block';
      }
      if (mainImg) mainImg.style.display = 'none';
    } else {
      if (mainVideo) { mainVideo.pause(); mainVideo.style.display = 'none'; }
      if (mainImg) {
        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('sizes');
        mainImg.setAttribute('src', thumb.dataset.imgUrl);
        mainImg.style.display = 'block';
      }
    }
  }

  function rebindThumbs() {
    const allThumbs = qsa('.thumb', thumbsWrap);
    allThumbs.forEach(t => {
      t.addEventListener('click', () => selectThumb(t, allThumbs));
    });
  }

  async function swapTo(url, pill) {
    const cleanUrl = url.split('#')[0];

    pills.forEach(p => {
      p.classList.remove('selected');
      p.setAttribute('aria-checked', 'false');
    });
    pill.classList.add('selected');
    pill.setAttribute('aria-checked', 'true');

    try {
      const res = await fetch(cleanUrl + '.js');
      if (!res.ok) throw new Error('Produkt konnte nicht geladen werden');
      const p = await res.json();
      const variant = p.variants[0];

      if (descEl)  descEl.innerHTML = p.description || '';
      if (variantInput) variantInput.value = variant.id;

      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? 'In den Warenkorb' : 'Ausverkauft';
      }

      if (priceWrap) {
        const cur = qs('.price-current', priceWrap);
        const cmp = qs('.price-compare', priceWrap);
        if (cur) cur.textContent = money(variant.price);
        if (cmp) {
          if (variant.compare_at_price > variant.price) {
            cmp.textContent = money(variant.compare_at_price);
            cmp.hidden = false;
          } else {
            cmp.hidden = true;
          }
        }
      }

      if (thumbsWrap && p.images && p.images.length) {
        /* Keep the video thumbs (section-level, same for every animal) */
        const videoThumbs = qsa('.thumb--video', thumbsWrap).map(el => el.cloneNode(true));

        thumbsWrap.innerHTML = p.images.slice(0, 20).map((src, i) => {
          const full  = src + (src.includes('?') ? '&width=900' : '?width=900');
          const small = src + (src.includes('?') ? '&width=150' : '?width=150');
          return `<div class="thumb${i === 0 ? ' active' : ''}" data-img-url="${full}">`
               + `<img src="${small}" loading="lazy" alt=""></div>`;
        }).join('');
        videoThumbs.forEach(el => thumbsWrap.appendChild(el));

        const allThumbs = qsa('.thumb', thumbsWrap);
        rebindThumbs();
        if (allThumbs.length) selectThumb(allThumbs[0], allThumbs);
      }

      history.pushState({}, '', cleanUrl);
      document.title = p.title + ' – ' + (window.Viorah?.shopName || document.title.split('–').pop().trim());

    } catch (err) {
      console.error('[Viorah] Animal switch failed, navigating instead:', err);
      window.location.href = url;
    }
  }

  pills.forEach(pill => {
    pill.addEventListener('click', e => {
      e.preventDefault();
      swapTo(pill.getAttribute('href'), pill);
    });
  });
}

/* ============================================================
   PRODUCT FORM – QUANTITY, ADD TO CART
   ============================================================ */
function initProductForm() {
  const form          = qs('#ProductForm');
  const variantInput  = qs('#variantId');
  const addBtn        = qs('#btnAddToCart');
  const qtyInput      = qs('#qtyInput');

  /* Quantity stepper */
  if (qtyInput) {
    const minus = qs('.qty-minus');
    const plus  = qs('.qty-plus');
    const clamp = () => {
      let n = parseInt(qtyInput.value, 10);
      if (!n || n < 1) n = 1;
      qtyInput.value = n;
      return n;
    };
    if (minus) minus.addEventListener('click', () => { qtyInput.value = Math.max(1, clamp() - 1); });
    if (plus)  plus.addEventListener('click', () => { qtyInput.value = clamp() + 1; });
    qtyInput.addEventListener('change', clamp);
  }

  /* ── ADD TO CART: JSON body + Modal ── */
  if (addBtn && form) {
    addBtn.addEventListener('click', async function(e) {
      e.preventDefault();
      const btn  = this;
      const orig = btn.textContent;
      btn.disabled = true;
      btn.textContent = '…';

      try {
        const variantId = parseInt(variantInput?.value || '0', 10);
        const quantity  = Math.max(1, parseInt(qtyInput?.value, 10) || 1);

        if (!variantId) throw new Error('Keine Variante gefunden');

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity })
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.description || 'Fehler ' + res.status);
        }

        btn.textContent = '✓ Hinzugefügt!';
        btn.style.background = '#5B8C7A';

        const cartRes  = await fetch('/cart.js');
        const cartData = await cartRes.json();
        ViorahCart.updateCount(cartData.item_count);

        const modal = document.getElementById('cartModal');
        if (modal) {
          modal.classList.add('open');
          const closeModal = () => {
            modal.classList.remove('open');
            btn.textContent = orig;
            btn.style.background = '';
            btn.disabled = false;
          };
          const continueBtn = modal.querySelector('.cart-modal-btn-continue');
          if (continueBtn) continueBtn.onclick = closeModal;
          modal.addEventListener('click', ev => { if (ev.target === modal) closeModal(); }, { once: true });
        } else {
          setTimeout(() => { btn.textContent = orig; btn.style.background = ''; btn.disabled = false; }, 2400);
        }

      } catch (err) {
        console.error('[Viorah] Cart error:', err);
        btn.textContent = err.message || 'Fehler';
        setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 4000);
      }
    });
  }
}

/* ============================================================
   PURCHASE TOGGLE (One-time / Subscribe)
   ============================================================ */
function initPurchaseToggle() {
  const btnOnetime   = qs('#btnOnetime');
  const btnSubscribe = qs('#btnSubscribe');
  if (!btnOnetime || !btnSubscribe) return;
  btnOnetime.addEventListener('click', () => {
    btnOnetime.classList.add('active');
    btnSubscribe.classList.remove('active');
  });
  btnSubscribe.addEventListener('click', () => {
    btnSubscribe.classList.add('active');
    btnOnetime.classList.remove('active');
  });
}

/* ============================================================
   COUNTDOWN TIMER
   ============================================================ */
function initTimer() {
  const hEl = qs('#timerH'), mEl = qs('#timerM'), sEl = qs('#timerS');
  if (!hEl) return;
  function tick() {
    const now  = new Date();
    const end  = new Date(now); end.setHours(23, 59, 59, 0);
    const diff = Math.max(0, Math.floor((end - now) / 1000));
    hEl.textContent = String(Math.floor(diff / 3600)).padStart(2, '0');
    mEl.textContent = String(Math.floor(diff % 3600 / 60)).padStart(2, '0');
    sEl.textContent = String(diff % 60).padStart(2, '0');
  }
  tick();
  setInterval(tick, 1000);
}

/* ============================================================
   GALLERY THUMBNAILS
   ============================================================ */
function initGallery() {
  const thumbs    = qsa('.thumb');
  const mainWrap  = qs('#GalleryMain') || qs('.gallery-main');
  const mainImg   = qs('#GalleryMainImg');
  const mainVideo = qs('#GalleryMainVideo');
  const prevBtn   = qs('#GalleryPrev');
  const nextBtn   = qs('#GalleryNext');

  function showVideo(url) {
    if (!mainVideo || !url) return;
    if (mainVideo.getAttribute('src') !== url) {
      mainVideo.setAttribute('src', url);
    }
    if (mainImg) mainImg.style.display = 'none';
    mainVideo.style.display = 'block';
  }

  function showImage(url) {
    if (mainVideo) {
      mainVideo.pause();
      mainVideo.style.display = 'none';
    }
    if (url && mainImg) {
      /* Must remove srcset — browser prefers srcset over src */
      mainImg.removeAttribute('srcset');
      mainImg.removeAttribute('sizes');
      mainImg.setAttribute('src', url);
      mainImg.style.display = 'block';
    }
  }

  function goTo(idx) {
    const clamped = (idx + thumbs.length) % thumbs.length;
    const thumb   = thumbs[clamped];
    if (!thumb) return;
    thumbs.forEach(t => t.classList.remove('active'));
    thumb.classList.add('active');
    thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

    if (thumb.dataset.videoUrl) {
      showVideo(thumb.dataset.videoUrl);
      return;
    }

    let url = thumb.dataset.imgUrl;
    if (!url) {
      const tImg = thumb.querySelector('img');
      if (tImg) url = tImg.src.replace(/width=\d+/, 'width=900');
    }
    showImage(url);
  }

  function activeIdx() { return thumbs.findIndex(t => t.classList.contains('active')); }

  thumbs.forEach((t, i) => t.addEventListener('click', () => goTo(i)));

  if (prevBtn) prevBtn.addEventListener('click', () => goTo(activeIdx() - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => goTo(activeIdx() + 1));

  /* Thumbnail strip arrows */
  const thumbsTrack = qs('#GalleryThumbs');
  const tPrev = qs('.gallery-thumbs-prev');
  const tNext = qs('.gallery-thumbs-next');
  if (thumbsTrack && tPrev) tPrev.addEventListener('click', () => thumbsTrack.scrollBy({ left: -180, behavior: 'smooth' }));
  if (thumbsTrack && tNext) tNext.addEventListener('click', () => thumbsTrack.scrollBy({ left: 180, behavior: 'smooth' }));

  /* Hide arrows if only 1 image */
  if (thumbs.length <= 1) {
    if (prevBtn) prevBtn.style.display = 'none';
    if (nextBtn) nextBtn.style.display = 'none';
  }

  /* Touch swipe */
  if (mainWrap) {
    let startX = 0;
    let startY = 0;
    mainWrap.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });
    mainWrap.addEventListener('touchmove', e => {
      const dx = Math.abs(e.touches[0].clientX - startX);
      const dy = Math.abs(e.touches[0].clientY - startY);
      if (dx > dy && dx > 8) e.preventDefault();
    }, { passive: false });
    mainWrap.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) < 20) return;
      goTo(diff > 0 ? activeIdx() + 1 : activeIdx() - 1);
    }, { passive: true });
  }
}

/* ============================================================
   FAQ ACCORDION
   ============================================================ */
function initFAQ() {
  qsa('.faq-question').forEach(btn => {
    btn.addEventListener('click', () => {
      const item   = btn.closest('.faq-item');
      const isOpen = item.classList.contains('open');
      qsa('.faq-item').forEach(i => i.classList.remove('open'));
      if (!isOpen) item.classList.add('open');
    });
  });
}

/* ============================================================
   SCROLL REVEAL
   ============================================================ */
function initReveal() {
  if (!window.IntersectionObserver) {
    qsa('.reveal').forEach(el => el.classList.add('visible'));
    return;
  }
  const obs = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); }
    });
  }, { threshold: 0.08 });
  qsa('.reveal').forEach(el => obs.observe(el));
}

/* ============================================================
   STAT RINGS (animated SVG circles)
   ============================================================ */
function initStatRings() {
  if (!window.IntersectionObserver) {
    qsa('.stat-fill').forEach(c => { c.style.strokeDashoffset = c.dataset.target; });
    return;
  }
  const obs = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.style.strokeDashoffset = e.target.dataset.target;
        obs.unobserve(e.target);
      }
    });
  }, { threshold: 0.4 });
  qsa('.stat-fill').forEach(el => obs.observe(el));
}

/* ============================================================
   LENA CHAT WIDGET
   ============================================================ */
function initChat() {
  const widget   = qs('#chatWidget');
  const toggle   = qs('#chatToggle');
  const messages = qs('#chatMessages');
  const input    = qs('#chatInput');
  const send     = qs('#chatSend');
  const chips    = qs('#chatChips');
  if (!widget || !toggle) return;

  toggle.addEventListener('click', () => {
    widget.classList.toggle('open');
    if (widget.classList.contains('open') && input) input.focus();
  });

  const replies = [
    'Das ist eine wunderbare Frage! SleepyHug eignet sich für Babys, die beim Einschlafen eine vertraute, gleichmäßige Berührung brauchen. Darf ich Ihnen mehr über die Klopf-Funktion erzählen?',
    'Mit der Fernbedienung lässt sich der Klopf-Rhythmus ganz einfach einstellen – sanft und gleichmäßig. Kann ich Ihnen bei der Auswahl des Tier-Designs helfen?',
    'Beim 2er-Set sparen Sie gegenüber dem Einzelkauf – ideal auch als Geschenk. Möchten Sie sich einen Platz auf der Warteliste sichern?',
    'Bei weiteren Fragen stehe ich gerne zur Verfügung – oder wenden Sie sich an support@viorah.ch. Gibt es noch etwas, womit ich Ihnen helfen kann?',
    'SleepyHug befindet sich aktuell in der finalen Entwicklungsphase, inklusive Sicherheits- und Materialprüfungen. Wartelisten-Mitglieder erfahren alle Neuigkeiten zuerst. Darf ich mehr erzählen?',
  ];
  let replyIdx = 0;

  function appendMsg(text, type) {
    const div = document.createElement('div');
    div.className = `msg ${type}`;
    div.textContent = text;
    messages.appendChild(div);
    messages.scrollTop = messages.scrollHeight;
  }

  function sendMessage(text) {
    text = text.trim();
    if (!text) return;
    appendMsg(text, 'user');
    if (input) input.value = '';

    const typing = document.createElement('div');
    typing.className = 'typing-indicator';
    typing.innerHTML = '<div class="typing-dot"></div><div class="typing-dot"></div><div class="typing-dot"></div>';
    messages.appendChild(typing);
    messages.scrollTop = messages.scrollHeight;

    setTimeout(() => {
      typing.remove();
      appendMsg(replies[replyIdx % replies.length], 'bot');
      replyIdx++;
    }, 900 + Math.random() * 500);
  }

  if (send)  send.addEventListener('click', () => sendMessage(input.value));
  if (input) input.addEventListener('keydown', e => { if (e.key === 'Enter') sendMessage(input.value); });

  if (chips) {
    chips.addEventListener('click', e => {
      const chip = e.target.closest('.chip');
      if (!chip) return;
      sendMessage(chip.textContent);
      chips.style.display = 'none';
    });
  }
}

/* ============================================================
   CART PAGE (quantity / remove)
   ============================================================ */
function initCartPage() {
  const form = qs('#CartForm');
  if (!form) return;

  async function setQuantity(key, quantity) {
    form.style.opacity = '.5';
    try {
      const cart = await ViorahCart.changeItem(key, quantity);
      ViorahCart.updateCount(cart.item_count);
      window.location.reload();
    } catch {
      form.style.opacity = '';
    }
  }

  form.addEventListener('click', e => {
    const minus = e.target.closest('.cr-qty-minus');
    const plus  = e.target.closest('.cr-qty-plus');
    const remove = e.target.closest('.cr-remove');

    if (minus || plus) {
      const row   = e.target.closest('.cart-row');
      const input = qs('.cr-qty-input', row);
      const next  = Math.max(0, (parseInt(input.value, 10) || 0) + (plus ? 1 : -1));
      setQuantity(row.dataset.key, next);
    }

    if (remove) {
      setQuantity(remove.dataset.key, 0);
    }
  });

  form.addEventListener('change', e => {
    if (!e.target.classList.contains('cr-qty-input')) return;
    const next = Math.max(0, parseInt(e.target.value, 10) || 0);
    setQuantity(e.target.dataset.key, next);
  });
}

/* ============================================================
   CART COUNT INIT
   ============================================================ */
function initCartCount() {
  const count = window.Viorah?.cartCount || 0;
  ViorahCart.updateCount(count);
}

/* ============================================================
   INIT ALL
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {
  initProductForm();
  initAnimalSwitch();
  initPurchaseToggle();
  initTimer();
  initGallery();
  initFAQ();
  initReveal();
  initStatRings();
  initChat();
  initCartCount();
  initCartPage();
});
